import { useSelection } from "@/contexts/SelectionContext";
import { useNestSlides } from "@/lib/hooks/slides/useNestSlides";
import type { SlideWithSubslides } from "@/lib/hooks/slides/useSlidesByLessonId";
import { useTags } from "@/lib/hooks/tags/useTags";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { closeNestSlideModal } from "@/store/uiSlice";
import { resolveSlideTemplate } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useState } from "react";
import { HiX } from "react-icons/hi";
import { MdPostAdd } from "react-icons/md";
import { SlideThumbnail } from "./SlideThumbnail";

interface NestSlideModalProps {
  mainSlides: SlideWithSubslides[];
}

/** Picks the main slide an existing slide joins, so the two always play together. */
export function NestSlideModal({ mainSlides }: NestSlideModalProps) {
  const { nestingSlides } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const { nestSlides, loading } = useNestSlides();
  const { clearAllSelections } = useSelection();
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const { tags } = useTags();

  if (!nestingSlides) return null;

  const nestingIds = new Set(nestingSlides.ids);

  const query = search.trim().toLowerCase();
  // A linked copy mirrors its original's group, so it can never take this slide in.
  const candidates = mainSlides.filter(
    (slide) =>
      !nestingIds.has(slide.id) &&
      !slide.isLinkedCopy &&
      (query === "" || slide.name.toLowerCase().includes(query)) &&
      (tagFilter === null || (slide.tagIds ?? []).includes(tagFilter))
  );

  // Only tags the candidates actually carry, so the filter never offers an empty result.
  const availableTags = tags.filter((tag) =>
    mainSlides.some((slide) => !nestingIds.has(slide.id) && (slide.tagIds ?? []).includes(tag.id))
  );

  const handleConfirm = async () => {
    if (!selectedParentId) return;
    try {
      await nestSlides(selectedParentId, nestingSlides.ids);
    } finally {
      dispatch(closeNestSlideModal());
      clearAllSelections();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6">
      {/* Nearly full screen: a lesson can hold hundreds of slides, and one column makes finding one a scroll. */}
      <div className="bg-surface rounded-2xl shadow-2xl w-full h-full max-w-[90rem] max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-200 dark:border-gray-800">
          <div className="p-2 rounded-xl bg-amber-500/10 text-amber-500">
            <MdPostAdd className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="card-title text-secondary-200">Make subslide</h2>
            <p className="caption text-gray-500 truncate">
              Pick the slide {nestingSlides.label} should play with.
            </p>
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label="Close"
            onClick={() => dispatch(closeNestSlideModal())}
          >
            <HiX className="w-5 h-5" />
          </IconButton>
        </div>

        <div className="flex items-center gap-3 px-6 pt-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search slides..."
            className="w-72 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent outline-none focus:ring-2 focus:ring-amber-400"
          />

          <div className="flex items-center gap-1.5 overflow-x-auto">
            <button
              type="button"
              onClick={() => setTagFilter(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors duration-200 ${
                tagFilter === null
                  ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"
                  : "border-gray-200 dark:border-gray-800 text-gray-500"
              }`}
            >
              All tags
            </button>
            {availableTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => setTagFilter(tag.id === tagFilter ? null : tag.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors duration-200 ${
                  tagFilter === tag.id
                    ? "border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300"
                    : "border-gray-200 dark:border-gray-800 text-gray-500"
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {candidates.length === 0 ? (
            <p className="caption text-gray-500">
              {query === "" && tagFilter === null
                ? "This lesson has no other slide to join."
                : "No slide matches this filter."}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 xl:grid-cols-8 gap-3 items-start">
              {candidates.map((slide) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => setSelectedParentId(slide.id)}
                  className={`flex flex-col gap-1.5 p-2 rounded-lg border text-left transition-colors duration-200 ${
                    selectedParentId === slide.id
                      ? "border-amber-400 ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-950/30"
                      : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900"
                  }`}
                >
                  {/* Names repeat across a lesson, so the thumbnail is what tells them apart. */}
                  <div className="pointer-events-none">
                    <SlideThumbnail slide={slide} handleCardClick={() => undefined} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate text-secondary-200">{slide.name}</p>
                    <p className="caption text-[0.65rem] text-gray-500 truncate">
                      {getTemplateLabel(resolveSlideTemplate(slide))}
                      {slide.children.length > 0 && ` · ${slide.children.length} subslides`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-800">
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-400"
            onClick={() => dispatch(closeNestSlideModal())}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedParentId || loading}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-amber-500 text-white font-semibold disabled:opacity-50"
          >
            Make subslide
          </button>
        </div>
      </div>
    </div>
  );
}
